import * as React from 'react';
import throttle from 'lodash.throttle';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {User} from 'interfaces';
import {Banner, Text, Breadcrumb, Section} from 'components/UIKit';
import {WhyExchange, InfoBar} from 'components/Marketplace';
import * as locales from './locales';
import styles from './Listing.scss';
import {Shop, ListingShop, ShopScreenShot, SocialLinks, ContactTrackingData} from './types';
import {getListingShopData, trackPageImageClick} from './utilities';
import {
  ListingTags,
  ImageViewer,
  ImageSwiper,
  ListingTrust,
  ListingShare,
  SoldListings,
  ContactModal,
  ContactMobile,
  ListingImages,
  ListingHeader,
  ListingTabNav,
  ListingContactFooter,
  ListingExpenses,
  ListingLongform,
  ListingIncludes,
  SellerInformation,
  SimilarListings,
  ListingTrafficPerf,
} from './components';

export interface ListingProps {
  shop: Shop;
  currentUser?: User;
  links: {
    [key: string]: string;
  };
  soldStores: Shop[];
  socialLinks: SocialLinks;
  contactData: any;
  isOwnStore: boolean;
  similarListings: Shop[];
  contactTracking: ContactTrackingData;
  authenticityToken: string;
  breadcrumbs: Breadcrumb[];
}

export interface ListingState {
  tabs: any;
  shop: ListingShop;
  images: ShopScreenShot[];
  isMobile?: boolean;
  isTablet?: boolean;
  activeTab?: string;
  soldStores: ListingShop[];
  trackedImage: boolean;
  selectedImage: ShopScreenShot | null;
  showImageViewer: boolean;
  showContactForm: boolean;
  similarListings: ListingShop[];
  mobileFormShowing: boolean;
}

// 751 corresponds to our CSS media queries that handle tablet changes. 750 is still rendered as mobile
const TABLET_MIN_WIDTH = 751;
const DESKTOP_MIN_WIDTH = 1080;

const TABS = {
  generalInformation: {
    id: 'GeneralInformation',
    isVisible: (shop) => shop.generalInformation.length > 0 && shop.isListingOne,
  },
  businessStory: {
    id: 'BusinessStory',
    isVisible: (shop) => shop.businessInformation.length && !shop.isListingOne,
  },
  performance: {
    id: 'Performance',
    isVisible: (shop) => !shop.starterShop,
  },
  expenses: {
    id: 'Expenses',
    isVisible: (shop) => shop.expenses.length,
  },
  saleIncludes: {
    id: 'SaleIncludes',
    isVisible: (shop) => shop.socialMedia.length || shop.saleIncludes.length,
  },
  sellersAdvice: {
    id: 'SellersAdvice',
    isVisible: (shop) => shop.sellerAdvice.length,
  },
};

function getTabId(i18n, id) {
  return i18n.translate(`${id}Id`);
}

class ListingComponent extends React.Component<ListingProps & WithI18nProps, ListingState> {
  anchorVisibilityMap: {[tabId: string]: boolean};

  handleResize = throttle(() => {
    this.setState({
      isMobile: window.innerWidth < TABLET_MIN_WIDTH,
      isTablet: window.innerWidth >= TABLET_MIN_WIDTH && window.innerWidth < DESKTOP_MIN_WIDTH,
    });
  }, 200);

  setActiveTab = throttle(() => {
    const {i18n} = this.props;
    const firstVisibleTab = Object.values(TABS).find(
      (tab) => this.anchorVisibilityMap[getTabId(i18n, tab.id)],
    );

    this.setState((prevState) => {
      return {
        ...prevState,
        activeTab: firstVisibleTab ? getTabId(i18n, firstVisibleTab.id) : prevState.activeTab || '',
      };
    });
  }, 200);

  constructor(props) {
    super(props);

    const {i18n, shop, soldStores = [], similarListings = []} = props;

    const parsedShop = getListingShopData(shop, i18n);
    const {allScreenshots = []} = parsedShop.screenshots;

    this.state = {
      tabs: this.getTabs(parsedShop),
      shop: parsedShop,
      images: allScreenshots,
      soldStores: soldStores && soldStores.map((shop) => getListingShopData(shop, i18n)),
      trackedImage: false,
      selectedImage: allScreenshots.length ? allScreenshots[0] : null,
      showImageViewer: false,
      showContactForm: false,
      similarListings:
        similarListings && similarListings.map((shop) => getListingShopData(shop, i18n)),
      mobileFormShowing: false,
    };

    this.anchorVisibilityMap = {};
  }

  handleFormVisibilityChange = (inView) => {
    this.setState({
      mobileFormShowing: inView,
    });
  };

  handleVisibilityChange = (tab, inView) => {
    this.anchorVisibilityMap[tab] = inView;
    this.setActiveTab();
  };

  getTabs = (shop) => {
    const {i18n} = this.props;

    return Object.values(TABS)
      .filter((tab) => tab.isVisible(shop))
      .map((tab) => ({
        id: getTabId(i18n, tab.id),
        label: i18n.translate(tab.id),
      }));
  };

  handleThumbnailClick = (selectedImage: ShopScreenShot) => this.setState({selectedImage});
  handleFullscreenClose = () => this.setState({showImageViewer: false});
  handleFullscreenImageClick = (selectedImage: ShopScreenShot) => this.setState({selectedImage});

  handleGalleryImageClick = () => {
    const {shop} = this.props;
    const {trackedImage} = this.state;

    if (!trackedImage) {
      trackPageImageClick(shop.title);
    }

    this.setState({showImageViewer: true, trackedImage: true});
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.setState({
      isMobile: window.innerWidth < TABLET_MIN_WIDTH,
      isTablet: window.innerWidth >= TABLET_MIN_WIDTH && window.innerWidth < DESKTOP_MIN_WIDTH,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleContactClick = () => {
    const {currentUser} = this.props;
    const {isMobile} = this.state;

    if (!currentUser) {
      location.hash = 'login';
      return;
    }

    if (!isMobile) {
      this.setState({showContactForm: true});
    }
  };

  handleContactCancel = () => {
    this.setState({showContactForm: false});
  };

  handleDomainClick = () => {
    const {shop} = this.props;
    const {trackedImage} = this.state;

    if (!trackedImage) {
      trackPageImageClick(shop.title);
    }

    this.setState({trackedImage: true});
  };

  handleTabClick = (activeTab) => this.setState({activeTab});

  render() {
    const {
      currentUser,
      i18n,
      links,
      socialLinks,
      isOwnStore,
      breadcrumbs,
      contactTracking,
      authenticityToken,
    } = this.props;

    const {
      tabs,
      shop,
      images,
      isMobile,
      isTablet,
      activeTab,
      soldStores,
      selectedImage,
      showContactForm,
      showImageViewer,
      similarListings,
      mobileFormShowing,
    } = this.state;

    const showBio = Boolean(shop.sellerInformation);
    const showSold = soldStores && soldStores.length > 0;
    const showShare = !shop.sold;
    const showSwiper = Boolean(isMobile && images.length);
    const showSimilar = similarListings && similarListings.length > 0;
    const showLifeSpan = Boolean(shop.lifeSpan);
    const showExpenses = shop.expenses.length > 0;
    const showIncludes = Boolean(shop.socialMedia.length || shop.saleIncludes.length);
    const showPerformance = !shop.starterShop;
    const showGeneralInfo = shop.generalInformation.length > 0 && shop.isListingOne;
    const showSellerAdvice = shop.sellerAdvice.length > 0;
    const showMobileContact = isMobile && !shop.sold;
    const showTabletGallery = Boolean(isTablet && images.length);
    const showSidebarGallery = Boolean(!isMobile && !isTablet && images.length);
    const showBusinessStory = shop.businessInformation.length > 0;
    const showTimeCommitment = typeof shop.timeCommitment === 'number';
    const showFullscreenImageViewer = !isMobile && showImageViewer;
    const screenshotAltText = `${shop.title} ${i18n.translate('screenshotAlt')}`;

    return (
      <>
        <InfoBar breadcrumbs={breadcrumbs} className={styles.InfoBar} />
        {showMobileContact ? (
          <ListingContactFooter shop={shop} user={currentUser} hide={mobileFormShowing} />
        ) : (
          <ContactModal
            show={showContactForm}
            shop={shop}
            user={currentUser}
            isOwnStore={isOwnStore}
            onClose={this.handleContactCancel}
            contactTracking={contactTracking}
            authenticityToken={authenticityToken}
          />
        )}
        <Section spacing="medium" className={styles.Container} fullWidth>
          <section className={styles.Main}>
            <ListingHeader
              shop={shop}
              metrics={shop.metrics}
              onDomainClick={this.handleDomainClick}
              onContactClick={this.handleContactClick}
            />
            {showTabletGallery && (
              <ListingImages
                images={images}
                selected={selectedImage as ShopScreenShot}
                altPrefix={screenshotAltText}
                thumbnailOrientation={isTablet ? 'vertical' : 'horizontal'}
                onImageClick={this.handleGalleryImageClick}
                onThumbnailClick={this.handleThumbnailClick}
              />
            )}
            {!isMobile && (
              <ListingTabNav
                tabs={tabs}
                selected={activeTab}
                onTabClick={this.handleTabClick}
                hideContact={shop.sold}
                onContactClick={this.handleContactClick}
              />
            )}
            <div className={styles.MobilePageContainer}>
              {showSwiper && <ImageSwiper images={images} altPrefix={screenshotAltText} />}
              <Section spacing="medium" fullWidth>
                {showBusinessStory && (
                  <ListingLongform
                    id={i18n.translate('BusinessStoryId')}
                    title={i18n.translate('businessStory')}
                    content={shop.businessInformation}
                    onVisibilityChange={this.handleVisibilityChange}
                  />
                )}
                {showLifeSpan && (
                  <Banner display="sparse" icon="calendar" iconSize={20}>
                    <Text resetMargin secondary text={i18n.translate('businessFounded')} />
                    &nbsp;
                    <Text
                      resetMargin
                      text={i18n.translate('lifeSpanAgo', {lifeSpan: shop.lifeSpan})}
                    />
                  </Banner>
                )}
                {showTimeCommitment && (
                  <Banner display="sparse" richIcon="clock" iconSize={20}>
                    <Text resetMargin secondary text={i18n.translate('timeToRun')} />
                    &nbsp;
                    <Text
                      resetMargin
                      text={i18n.translate('hoursPerWeek', {
                        hoursNum: shop.timeCommitment,
                        hoursWord: i18n.translate(shop.timeCommitment === 1 ? 'hour' : 'hours'),
                      })}
                    />
                  </Banner>
                )}
              </Section>
              {showGeneralInfo && (
                <Section spacing="medium" fullWidth>
                  <ListingLongform
                    id={i18n.translate('GeneralInformationId')}
                    title={i18n.translate('GeneralInformation')}
                    content={shop.generalInformation}
                    onVisibilityChange={this.handleVisibilityChange}
                  />
                </Section>
              )}
              {showPerformance && (
                <ListingTrafficPerf
                  id={i18n.translate('PerformanceId')}
                  charts={shop.charts}
                  metrics={shop.trafficAndPerformance}
                  profitDetails={shop.profitDetails}
                  onVisibilityChange={this.handleVisibilityChange}
                />
              )}
              {showExpenses && (
                <ListingExpenses
                  id={i18n.translate('ExpensesId')}
                  expenses={shop.expenses}
                  onVisibilityChange={this.handleVisibilityChange}
                />
              )}
              {showIncludes && (
                <ListingIncludes
                  id={i18n.translate('SaleIncludesId')}
                  socialMediaAccounts={shop.socialMedia}
                  includes={shop.saleIncludes}
                  mailingList={shop.mailingListCount}
                  socialFollowers={shop.socialFollowerCount}
                  onVisibilityChange={this.handleVisibilityChange}
                  isPrivate={shop.isPrivate}
                  isStarterShop={shop.starterShop}
                />
              )}
              {showSellerAdvice && (
                <Section spacing="medium" fullWidth>
                  <ListingLongform
                    id={i18n.translate('SellersAdviceId')}
                    title={i18n.translate('SellersAdvice')}
                    content={shop.sellerAdvice}
                    onVisibilityChange={this.handleVisibilityChange}
                  />
                </Section>
              )}
            </div>
          </section>
          <section className={styles.Aside}>
            {showSidebarGallery && (
              <ListingImages
                images={images}
                selected={selectedImage as ShopScreenShot}
                altPrefix={screenshotAltText}
                thumbnailOrientation={isTablet ? 'vertical' : 'horizontal'}
                onImageClick={this.handleGalleryImageClick}
                onThumbnailClick={this.handleThumbnailClick}
              />
            )}
            {showFullscreenImageViewer && (
              <ImageViewer
                images={images}
                onClose={this.handleFullscreenClose}
                showing
                selected={selectedImage as ShopScreenShot}
                altPrefix={screenshotAltText}
                onImageSelect={this.handleFullscreenImageClick}
              />
            )}
            {showShare && <ListingShare links={socialLinks} />}
            {showBio && (
              <Section spacing="small" fullWidth>
                <SellerInformation sellerInfo={shop.sellerInformation} />
              </Section>
            )}
            <ListingTags tags={shop.tags} />
            {showSimilar && <SimilarListings similar={similarListings} reference={shop} />}
            {showSold && <SoldListings sold={soldStores} link={links.soldStores} />}
            {showMobileContact && Boolean(currentUser) && (
              <ContactMobile
                id="ContactForm"
                shop={shop}
                user={currentUser}
                isOwnStore={isOwnStore}
                contactTracking={contactTracking}
                authenticityToken={authenticityToken}
                onVisibilityChange={this.handleFormVisibilityChange}
              />
            )}
            <ListingTrust link={links.howItWorks} />
          </section>
        </Section>
        <WhyExchange />
      </>
    );
  }
}

export const Listing = withI18n({
  id: 'Listing',
  fallback: locales.en,
})(ListingComponent);

export default Listing;
